import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import ModalComponent from '../../../helper_components/ModalComponent';
import GrowLoader from '../../../layout/shared/GrowLoader';
import useLinkPromotionsWithAgreementsModal from '../customHooks/useLinkPromotionsWithAgreementsModal';
import { set } from 'lodash';

const LinkPromotionWithAgreementModal = ({
  isModalShowing,
  setModalShowing,
  expedientId,
  document,
}) => {
  const { loading, error, handleSubmit, promotions, setPromotions } =
    useLinkPromotionsWithAgreementsModal({
      isModalShowing,
      setModalShowing,
      expedientId,
      document,
    });

  const [selectedOptions, setSelectedOptions] = useState([]);

  const promotionOptions = promotions.map((promotion) => ({
    value: promotion.id,
    label: promotion.title,
  }));

  const handleSelectChange = (selectedOptions) => {
    setSelectedOptions(selectedOptions || []);
    setPromotions(
      promotions.map((promotion) => ({
        ...promotion,
        selected: selectedOptions
          ? selectedOptions.some((option) => option.value === promotion.id)
          : false,
      }))
    );
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : null,
      '&:hover': {
        borderColor: '#80bdff',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
      border: '1px solid #b8daff',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#004085',
      fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#004085',
      ':hover': {
        backgroundColor: '#dc3545',
        color: 'white',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
  };

  const documentName =
    document && document.alias
      ? document.alias
      : document && document.filename
        ? document.filename
        : 'Sin nombre';

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (selectedOptions.length === 0) {
      alert('Debe seleccionar al menos una promoción');
      return;
    }

    handleSubmit(e);
  };

  const handleCancel = () => {
    setSelectedOptions([]);
    setPromotions(
      promotions.map((promotion) => ({
        ...promotion,
        selected: false,
      }))
    );
    setModalShowing(false);
  };

  const modalHeader = (
    <div className="d-flex align-items-center">Vincular Promoción con Acuerdo</div>
  );

  if (loading) {
    const loadingBody = (
      <div className="text-center py-4">
        <GrowLoader />
        <p className="mt-3 mb-0">Cargando promociones...</p>
      </div>
    );

    const loadingFooter = (
      <Button variant="secondary" onClick={() => setModalShowing(false)}>
        Cancelar
      </Button>
    );

    return (
      <ModalComponent
        show={isModalShowing}
        onHide={() => setModalShowing(false)}
        header={modalHeader}
        body={loadingBody}
        footer={loadingFooter}
        size="lg"
        canClose={true}
      />
    );
  }

  if (error) {
    const errorBody = (
      <div className="alert alert-danger">
        <h5>
          <i className="fas fa-exclamation-triangle me-2"></i>Error al cargar las promociones
        </h5>
        <p className="mb-0">{error}</p>
      </div>
    );

    const errorFooter = (
      <Button variant="secondary" onClick={() => setModalShowing(false)}>
        Cerrar
      </Button>
    );

    return (
      <ModalComponent
        show={isModalShowing}
        onHide={() => setModalShowing(false)}
        header={modalHeader}
        body={errorBody}
        footer={errorFooter}
        size="lg"
        canClose={true}
      />
    );
  }

  const modalBody = (
    <Form onSubmit={handleFormSubmit}>
      <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
        <h6 className="mb-2">Acuerdo seleccionado:</h6>
        <p className="mb-0">
          <strong>{documentName}</strong>
        </p>
      </div>

      <Form.Group className="mb-4">
        <Form.Label className="mb-3">
          <i className="fas fa-list-check text-info me-2"></i>
          <strong>Seleccione una o más promociones para vincular:</strong>
        </Form.Label>

        {promotionOptions.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            No hay promociones disponibles para vincular.
          </div>
        ) : (
          <>
            <Select
              isMulti
              options={promotionOptions}
              value={selectedOptions}
              onChange={handleSelectChange}
              placeholder="Seleccionar promociones..."
              noOptionsMessage={() => 'No hay opciones disponibles'}
              loadingMessage={() => 'Cargando...'}
              styles={customStyles}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isSearchable={true}
              isClearable={true}
            />

            {/* Lista de promociones seleccionadas */}
            {selectedOptions.length > 0 && (
              <div
                className="mt-3 p-3"
                style={{
                  backgroundColor: '#e8f4fd',
                  borderRadius: '4px',
                  border: '1px solid #b8daff',
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <small className="text-info mb-0">
                    <strong>{selectedOptions.length}</strong> promoción(es) seleccionada(s):
                  </small>
                </div>
                <div className="row">
                  {selectedOptions.map((option) => (
                    <div key={option.value} className="col-12 col-md-6 mb-1">
                      <small
                        className="text-muted d-block text-truncate"
                        style={{ marginLeft: '10px' }}
                      >
                        • {option.label}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Form.Group>
    </Form>
  );

  const modalFooter = (
    <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
      <Button variant="error" onClick={() => handleCancel()} type="button">
        <i className="fas fa-times" style={{ marginRight: '4px' }}></i>
        <p className="mb-0 d-inline">Cancelar</p>
      </Button>
      <Button
        variant="primary"
        onClick={handleFormSubmit}
        disabled={loading || selectedOptions.length === 0}
      >
        {loading ? (
          <>
            <GrowLoader small /> Vinculando...
          </>
        ) : (
          <>Vincular {selectedOptions.length > 0 && `(${selectedOptions.length})`}</>
        )}
      </Button>
    </div>
  );

  return (
    <ModalComponent
      show={isModalShowing}
      onHide={() => setModalShowing(false)}
      header={modalHeader}
      body={modalBody}
      footer={modalFooter}
      size="lg"
      canClose={true}
      scroll={true}
      bodyStyle={{ height: '60vh', overflowY: 'auto' }}
    />
  );
};

export default LinkPromotionWithAgreementModal;
